import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Notes — the document/knowledge surface (the "Notion-like" capability).
 *
 * A Note is an ordered list of typed BLOCKS (paragraph, heading, to-do, bullet,
 * quote, code), plus tags. Notes link to each other with wiki links written as
 * [[Other Note Title]] inside block text; those links are resolved into a
 * directed knowledge graph with backlinks — pure and deterministic, so the
 * graph is always a faithful view of the notes rather than a separate store.
 *
 * Per the source-preservation rule, imported notes are preserved as
 * SourceRecords first; a Note is the reviewed, canonical document.
 */

export type BlockType = 'paragraph' | 'heading' | 'todo' | 'bullet' | 'quote' | 'code';
export const BLOCK_TYPES: readonly BlockType[] = ['paragraph', 'heading', 'todo', 'bullet', 'quote', 'code'] as const;

export interface Block {
  readonly id: string;
  readonly type: BlockType;
  readonly text: string;
  /** Only meaningful for `todo` blocks. */
  readonly checked: boolean;
}

export interface Note extends RecordEnvelope {
  readonly kind: 'note';
  readonly title: string;
  readonly blocks: readonly Block[];
  readonly tags: readonly string[];
}

export interface NoteInput {
  readonly title: string;
  readonly blocks?: readonly Block[];
  readonly tags?: readonly string[];
  readonly privacyClass?: PrivacyClass;
}

export interface NotePatch {
  readonly title?: string;
  readonly blocks?: readonly Block[];
  readonly tags?: readonly string[];
}

let blockCounter = 0;
/** Create a block with a stable-enough local id. Pure-ish (monotonic counter). */
export function makeBlock(type: BlockType, text = '', checked = false): Block {
  blockCounter += 1;
  return { id: `b${Date.now().toString(36)}-${blockCounter}`, type, text, checked };
}

const WIKI_LINK = /\[\[([^\]]+)\]\]/g;

/** Extract wiki-link targets ([[Title]]) from a string. Pure. */
export function extractWikiLinks(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(WIKI_LINK)) {
    const title = m[1]?.trim();
    if (title) out.push(title);
  }
  return out;
}

/** All unique wiki-link targets referenced anywhere in a note's blocks. */
export function noteWikiLinks(note: Pick<Note, 'blocks'>): string[] {
  const seen = new Set<string>();
  for (const b of note.blocks) {
    for (const link of extractWikiLinks(b.text)) seen.add(link.toLowerCase());
  }
  return [...seen];
}

/** Plain-text rendering of a note for previews and search indexing. */
export function notePlainText(note: Pick<Note, 'title' | 'blocks'>): string {
  return [note.title, ...note.blocks.map((b) => b.text)].join('\n').trim();
}

/** Count of completed vs total to-do blocks in a note. */
export function noteTodoProgress(note: Pick<Note, 'blocks'>): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const b of note.blocks) {
    if (b.type === 'todo') {
      total += 1;
      if (b.checked) done += 1;
    }
  }
  return { done, total };
}

export interface GraphNode {
  readonly id: EntityId;
  readonly title: string;
  /** Number of other notes linking TO this note. */
  readonly backlinks: number;
}

export interface GraphEdge {
  readonly fromId: EntityId;
  readonly toId: EntityId;
}

export interface KnowledgeGraph {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

/**
 * Build a directed knowledge graph from notes. Edges are resolved by matching a
 * note's [[wiki links]] to other notes' titles (case-insensitive). Unresolved
 * links (to not-yet-created notes) are simply omitted — never invented.
 */
export function buildKnowledgeGraph(notes: readonly Note[]): KnowledgeGraph {
  const byTitle = new Map<string, EntityId>();
  for (const n of notes) byTitle.set(n.title.trim().toLowerCase(), n.id);

  const edges: GraphEdge[] = [];
  const backlinkCount = new Map<string, number>();
  for (const note of notes) {
    const seenTargets = new Set<string>();
    for (const link of noteWikiLinks(note)) {
      const toId = byTitle.get(link);
      if (!toId || String(toId) === String(note.id)) continue;
      const key = `${String(note.id)}->${String(toId)}`;
      if (seenTargets.has(key)) continue;
      seenTargets.add(key);
      edges.push({ fromId: note.id, toId });
      backlinkCount.set(String(toId), (backlinkCount.get(String(toId)) ?? 0) + 1);
    }
  }

  const nodes: GraphNode[] = notes.map((n) => ({
    id: n.id,
    title: n.title,
    backlinks: backlinkCount.get(String(n.id)) ?? 0,
  }));

  return { nodes, edges };
}
